import React from 'react';
import { FormControl, FormLabel, Input } from '@chakra-ui/react';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import PropTypes from 'prop-types';

const selectStyles = {
  control: (base, state) => ({
    ...base,
    width: '100%',
    height: '48px',
    borderRadius: '12px',
    fontSize: '0.875rem',
    border: '1px solid #e0e5f2',
    backgroundColor: '#fff',
    color: '#203946',
    fontWeight: '500',
    boxShadow: state.isFocused ? '0 0 0 1px #e0e5f2' : 'none',
    opacity: 1,
    cursor: state.isDisabled ? 'default' : 'pointer',
    pointerEvents: state.isDisabled ? 'none' : 'auto',
  }),
  placeholder: (base) => ({
    ...base,
    paddingLeft: '10px',
  }),
  singleValue: (base) => ({
    ...base,
    paddingLeft: '10px',
    color: '#203946',
  }),
  dropdownIndicator: (base) => ({
    ...base,
    color: '#203946',
  }),
  valueContainer: (base) => ({
    ...base,
    paddingLeft: '10px',
  }),
  indicatorSeparator: (base) => ({
    ...base,
    backgroundColor: '#e0e5f2',
  }),
  option: (base, { isFocused, isSelected }) => ({
    ...base,
    backgroundColor: isSelected ? '#35505e' : isFocused ? '#f1f1f1' : '#fff',
    color: isSelected ? '#fff' : '#000',
    cursor: 'pointer',
  }),
};

const CommonReactSelect = ({
  label,
  isRequired,
  type,
  options,
  value,
  onChange,
  error,
  register,
  name,
  getOptionLabel,
  getOptionValue,
  isMulti,
  isDisabled,
  placeholderValue,
  onMenuScrollToBottom,
  onInputChange,
  creatable,
  onCreateOption,
  getNewOptionData,
  formatCreateLabel,
}) => {
  const getErrorMessage = (error) => {
    if (!error) return null;
    if (Array.isArray(error)) {
      // For array errors, check if there's a root message or use a default
      return error.message || 'Invalid selection';
    }
    // For object errors, return the message property
    return error.message;
  };

  const errorMessage = getErrorMessage(error);

  // Props shared by both the plain and creatable react-select variants.
  const sharedProps = {
    isMulti: isMulti ? isMulti : false,
    options,
    value,
    onChange,
    placeholder: `Select ${label ? label : placeholderValue}`,
    getOptionLabel,
    getOptionValue,
    onMenuScrollToBottom,
    onInputChange,
    styles: selectStyles,
    isDisabled,
  };

  const renderSelect = () => {
    if (creatable) {
      return (
        <CreatableSelect
          {...sharedProps}
          onCreateOption={onCreateOption}
          getNewOptionData={getNewOptionData}
          formatCreateLabel={formatCreateLabel || ((input) => `Use "${input}"`)}
        />
      );
    }
    return <Select {...sharedProps} />;
  };

  return (
    <FormControl isInvalid={!!error}>
      <FormLabel display="flex" ms="4px" fontSize="sm" fontWeight="500">
        {label}
        {isRequired && <span style={{ color: 'red' }}>*</span>}
      </FormLabel>
      {type === 'select' ? (
        renderSelect()
      ) : (
        <Input
          type={type}
          variant="auth"
          fontSize="sm"
          placeholder={label}
          mb="24px"
          fontWeight="500"
          size="lg"
          {...register(name)}
        />
      )}
      {errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>}
    </FormControl>
  );
};

CommonReactSelect.propTypes = {
  label: PropTypes.string.isRequired,
  isRequired: PropTypes.bool,
  type: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  value: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  onChange: PropTypes.func.isRequired,
  error: PropTypes.oneOfType([PropTypes.object, PropTypes.array]), // Updated to accept both object and array
  register: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  getOptionLabel: PropTypes.func.isRequired,
  getOptionValue: PropTypes.func.isRequired,
  isMulti: PropTypes.bool,
  isDisabled: PropTypes.bool,
  placeholderValue: PropTypes.string,
  onMenuScrollToBottom: PropTypes.func,
  onInputChange: PropTypes.func,
  creatable: PropTypes.bool,
  onCreateOption: PropTypes.func,
  getNewOptionData: PropTypes.func,
  formatCreateLabel: PropTypes.func,
};

export default CommonReactSelect;
